import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Loading, useGetIdentity, useGetList } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { JustifiedAction } from '../actions/JustifiedAction';
import type {
  CmsPage,
  ComplianceLegalReference,
  ComplianceStateToggle,
  ComplianceThreshold,
} from '../dataProvider';
import {
  LEGAL_REFERENCE_KEYS,
  isLegalSlug,
  legalReferenceLabel,
} from './complianceModel';

/**
 * The US compliance settings surface (D-settings-s4-us-compliance, Phase 9 S4), the LAST
 * and HIGHEST-CONSEQUENCE Settings surface. Three config sections in the S1 shape, all
 * US-ONLY (fixed market 'us', no region scoping, no cross-region apply-both):
 *
 *   A. The 1099-K reporting thresholds, ONE EDITABLE ROW PER YEAR. This is the LIVE
 *      connection: the tax report READS these rows as its crossing gate, so editing a
 *      year's threshold flows through to which members it lists.
 *   B. The per-state privacy-regime toggles, a SINGLE enabled boolean per state (the
 *      opt-out / do-not-sell regime on or off). The consumer is the migration seam.
 *   C. The privacy / terms REFERENCES, two slug pointers into the CMS legal pages (kind
 *      'legal'), soft-validated (warn if a slug does not resolve, never a block). NOT a
 *      document store: the CMS owns the content.
 *
 * Every change FORCES a reason (JustifiedAction, the reason rides into the audit diff via
 * the chokepoint). Gated compliance_settings:write; the surface is role-gated to compliance
 * and super_admin (the tax-report precedent), so editor and viewer never reach it.
 */

const nowIso = () => new Date().toISOString();

/** One editable 1099-K threshold year (the LIVE tax-report consumer). */
const ThresholdRow = ({
  row,
  email,
  canWrite,
}: {
  row: ComplianceThreshold;
  email: string;
  canWrite: boolean;
}) => {
  const [value, setValue] = useState(String(row.thresholdUsd));
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      useFlexGap
      flexWrap="wrap"
    >
      <Typography variant="body2" sx={{ minWidth: 64 }}>
        {row.year}
      </Typography>
      <TextField
        label="Threshold (USD)"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        type="number"
        margin="dense"
        size="small"
        sx={{ minWidth: 200 }}
        disabled={!canWrite}
        inputProps={{ 'data-testid': `threshold-input-${row.year}` }}
      />
      {canWrite && (
        <JustifiedAction
          resource="complianceThresholds"
          capability="compliance_settings:write"
          id={row.id}
          label={`Save ${row.year} threshold`}
          dialogTitle={`Change the ${row.year} 1099-K threshold`}
          dialogIntro="This threshold drives the 1099-K tax report. Record why it is changing."
          buildData={(reason) => ({
            thresholdUsd: Number(value),
            lastChangeReason: reason,
            updatedBy: email,
            updatedAt: nowIso(),
          })}
          previousData={row}
          successMessage="Threshold saved"
        />
      )}
    </Stack>
  );
};

/** One US state privacy-regime toggle (a single enabled boolean). */
const StateToggleRow = ({
  row,
  email,
  canWrite,
}: {
  row: ComplianceStateToggle;
  email: string;
  canWrite: boolean;
}) => (
  <Stack
    direction="row"
    spacing={2}
    alignItems="center"
    useFlexGap
    flexWrap="wrap"
  >
    <Box sx={{ minWidth: 220 }}>
      <Typography variant="body2">
        {row.stateCode} ({row.lawName})
      </Typography>
    </Box>
    <Chip
      size="small"
      label={row.enabled ? 'On' : 'Off'}
      color={row.enabled ? 'success' : 'default'}
      variant={row.enabled ? 'filled' : 'outlined'}
      data-testid={`state-chip-${row.stateCode}`}
    />
    {canWrite && (
      <JustifiedAction
        resource="complianceStateToggles"
        capability="compliance_settings:write"
        id={row.id}
        label={row.enabled ? 'Disable' : 'Enable'}
        color={row.enabled ? 'warning' : 'success'}
        dialogTitle={`${row.enabled ? 'Disable' : 'Enable'} ${row.stateCode} (${row.lawName})`}
        dialogIntro="Enabling or disabling a state's privacy regime is recorded with a reason."
        buildData={(reason) => ({
          enabled: !row.enabled,
          lastChangeReason: reason,
          updatedBy: email,
          updatedAt: nowIso(),
        })}
        previousData={row}
        successMessage="State toggle updated"
      />
    )}
  </Stack>
);

/** One privacy / terms REFERENCE (a slug pointer into the CMS legal pages). */
const LegalReferenceRow = ({
  row,
  email,
  canWrite,
  cmsPages,
}: {
  row: ComplianceLegalReference;
  email: string;
  canWrite: boolean;
  cmsPages: CmsPage[];
}) => {
  const [slug, setSlug] = useState(row.cmsSlug);
  // Soft subset-validation (the S3 posture): warn live when the slug does not resolve to
  // a CMS legal page. It STILL saves (the CMS owns the document; S4 only points at it).
  const resolves = isLegalSlug(slug, cmsPages);
  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
      >
        <Box sx={{ minWidth: 180 }}>
          <Typography variant="body2">
            {legalReferenceLabel(row.referenceKey)}
          </Typography>
        </Box>
        <TextField
          label="CMS legal page slug"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          margin="dense"
          size="small"
          sx={{ minWidth: 220 }}
          disabled={!canWrite}
          inputProps={{ 'data-testid': `legal-input-${row.referenceKey}` }}
        />
        {canWrite && (
          <JustifiedAction
            resource="complianceLegalReferences"
            capability="compliance_settings:write"
            id={row.id}
            label={`Save ${row.referenceKey} reference`}
            dialogTitle={`Change the ${legalReferenceLabel(row.referenceKey)} reference`}
            dialogIntro="This points at the canonical CMS legal page. Record why it is changing."
            buildData={(reason) => ({
              cmsSlug: slug.trim(),
              lastChangeReason: reason,
              updatedBy: email,
              updatedAt: nowIso(),
            })}
            previousData={row}
            successMessage="Reference saved"
          />
        )}
      </Stack>
      {!resolves && (
        <Alert
          severity="warning"
          sx={{ mt: 1 }}
          data-testid={`legal-warning-${row.referenceKey}`}
        >
          The slug &quot;{slug.trim()}&quot; does not resolve to a CMS legal
          page (kind legal). The compliance backend and the app will not find
          the document. You can still save.
        </Alert>
      )}
    </Box>
  );
};

const ComplianceSettingsInner = ({
  thresholds,
  states,
  references,
  cmsPages,
}: {
  thresholds: ComplianceThreshold[];
  states: ComplianceStateToggle[];
  references: ComplianceLegalReference[];
  cmsPages: CmsPage[];
}) => {
  const { identity } = useGetIdentity();
  const { role, accessibleRegions } = useAccessContext();
  const email = String(identity?.email ?? identity?.id ?? 'unknown');

  const canWrite = hasAccess(
    { role, accessibleRegions },
    { capability: 'compliance_settings:write' },
  );

  // Thresholds most recent year first; references in the canonical role order.
  const thresholdRows = useMemo(
    () => [...thresholds].sort((a, b) => b.year - a.year),
    [thresholds],
  );
  const referenceRows = useMemo(
    () =>
      [...references].sort(
        (a, b) =>
          LEGAL_REFERENCE_KEYS.indexOf(
            a.referenceKey as (typeof LEGAL_REFERENCE_KEYS)[number],
          ) -
          LEGAL_REFERENCE_KEYS.indexOf(
            b.referenceKey as (typeof LEGAL_REFERENCE_KEYS)[number],
          ),
      ),
    [references],
  );

  return (
    <Box sx={{ maxWidth: 860 }} data-testid="compliance-settings-page">
      <Typography variant="overline" color="text.secondary">
        Compliance
      </Typography>
      <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
        US compliance settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        US-only regulatory configuration. Every change is audited and records a
        reason. The 1099-K thresholds drive the tax report.
      </Typography>

      {/* Section A: 1099-K thresholds (the LIVE tax-report consumer, one row per year). */}
      <Card sx={{ mb: 2 }} data-testid="compliance-thresholds">
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            1099-K reporting thresholds
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The IRS reporting threshold per tax year. The tax report reads these
            figures as its crossing gate, so a change flows through to which
            members it lists.
          </Typography>
          <Stack spacing={2}>
            {thresholdRows.map((row) => (
              <ThresholdRow
                key={row.id}
                row={row}
                email={email}
                canWrite={canWrite}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Section B: per-state privacy toggles (single enabled boolean per state). */}
      <Card sx={{ mb: 2 }} data-testid="compliance-states">
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            State privacy regimes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Whether each US state&apos;s opt-out / do-not-sell regime is
            enabled. The app enforces the obligations of an enabled regime.
          </Typography>
          <Stack spacing={2}>
            {states.map((row) => (
              <StateToggleRow
                key={row.id}
                row={row}
                email={email}
                canWrite={canWrite}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Section C: privacy / terms references (slug pointers into the CMS legal pages). */}
      <Card sx={{ mb: 2 }} data-testid="compliance-legal">
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Privacy and terms references
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The canonical US legal documents live in the CMS. These are pointers
            (a page slug), not the documents themselves.
          </Typography>
          <Stack spacing={2}>
            {referenceRows.map((row) => (
              <LegalReferenceRow
                key={row.id}
                row={row}
                email={email}
                canWrite={canWrite}
                cmsPages={cmsPages}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

const ALL = {
  pagination: { page: 1, perPage: 100 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

/** The surface without the access wrapper. Exported for testing. */
export const ComplianceSettingsView = () => {
  const thresholds = useGetList<ComplianceThreshold>(
    'complianceThresholds',
    ALL,
  );
  const states = useGetList<ComplianceStateToggle>(
    'complianceStateToggles',
    ALL,
  );
  const references = useGetList<ComplianceLegalReference>(
    'complianceLegalReferences',
    ALL,
  );
  const cmsPages = useGetList<CmsPage>('cmsPages', ALL);

  if (
    thresholds.isLoading ||
    states.isLoading ||
    references.isLoading ||
    cmsPages.isLoading
  ) {
    return <Loading />;
  }
  return (
    <ComplianceSettingsInner
      thresholds={thresholds.data ?? []}
      states={states.data ?? []}
      references={references.data ?? []}
      cmsPages={cmsPages.data ?? []}
    />
  );
};

/**
 * Gated entry point mounted at /compliance-settings. ROLE-gated to compliance and
 * super_admin (the tax-report precedent): a viewer's read-everywhere '*:read' cannot
 * reach it and editor is denied. The write affordances additionally check
 * compliance_settings:write (which those two roles hold).
 */
export const ComplianceSettingsPage = () => (
  <RequireAccess roles={['compliance', 'super_admin']}>
    <ComplianceSettingsView />
  </RequireAccess>
);
